import {
  Box,
  Button,
  Grid,
  Typography,
  AppBar,
  Toolbar,
  useTheme,
  Autocomplete,
  TextField,
  Dialog,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ptBR from "date-fns/locale/pt-BR";
import { useEffect, useState, useMemo, useRef } from "react";
import {
  darkenColor,
  formatCnpj,
  nameAbbreviation,
  dataGridTexts,
} from "../../../../../utils";
import { useBackofficeService } from "../../../../../use-cases/backoffice";
import { useAppSelector } from "../../../../../application/states/hooks";
import { FilterList } from "@mui/icons-material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useReportCompaniesService } from "../../../../../use-cases/report-companies";
import { IReportCompanies } from "../../../../../application/models";
import { xlsxGenerator } from "../../../../../utils/convert-to-xlsx/xslxGenerator";
import { generateGuid } from "../../../../../utils/general-utils";
import { useLoadingService } from "../../../../../use-cases/loading"; 

export const SetPeriodReportCompaniesPage = () => {
  const { getPeriodReportCompanies, checkReportStatus, resetReportCompanies } = useReportCompaniesService();
  const { startLoading, stopLoading } = useLoadingService();

  const theme = useTheme();
  const { user } = useAppSelector((state) => state);
  const { lurikProcess } = useAppSelector((state) => state.lurikProcess);

    // Refs para os scrollbars
  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);



  const clearAllFilters = () => {
  // Limpa datas
  setConsumptionInfoStartDate(null);
  setConsumptionInfoEndDate(null);
  
  // Limpa company selection
  setSelectedCompanyOption(undefined);
  setResetSelectedCompany()
  
  // Limpa outros estados
  setReportGenerated(false);
  resetConsumptionReport();
};

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  const companyList = useAppSelector(
    (state) => state.backofficeCompany.companyList
  );
  const selectedCompany = useAppSelector(
    (state) => state.backofficeCompany.selectedCompany
  );
  const { resetConsumptionReport, selectCompany, setResetSelectedCompany } = useBackofficeService();
  const { company } = useAppSelector((state) => state.company);
  const resaleConfig = useAppSelector(
    (state) => state.resaleConfig.resaleConfig
  );

  const [consumptionInfoStartDate, setConsumptionInfoStartDate] =
    useState<Date | null>(null);
  const [consumptionInfoEndDate, setConsumptionInfoEndDate] =
    useState<Date | null>(null);
  const [selectedCompanyOption, setSelectedCompanyOption] = useState<
    (typeof companyList)[0] | null | undefined
  >(null);
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [reportGenerated, setReportGenerated] = useState(false);
  
  const { reportCompanies } = useAppSelector(
    (state) => state.reportCompanies
  );

  // Sincroniza seleção local com Redux
  useEffect(() => {
    if (selectedCompany) {
      setResetSelectedCompany()
    } else {
      setSelectedCompanyOption(undefined);
    }
  }, []);

    // Efeito para sincronizar os scrollbars
  useEffect(() => {
    const topScroll = topScrollRef.current;
    const bottomScroll = bottomScrollRef.current;

    if (!topScroll || !bottomScroll) return;

    const handleTopScroll = () => {
      if (bottomScroll) {
        bottomScroll.scrollLeft = topScroll.scrollLeft;
      }
    };

    const handleBottomScroll = () => {
      if (topScroll) {
        topScroll.scrollLeft = bottomScroll.scrollLeft;
      }
    };

    topScroll.addEventListener('scroll', handleTopScroll);
    bottomScroll.addEventListener('scroll', handleBottomScroll);

    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll);
      bottomScroll.removeEventListener('scroll', handleBottomScroll);
    };
  }, [openReportDialog]); // Reexecuta quando o modal abre/fecha

  // Polling para verificar status do processo e buscar dados quando completo
  useEffect(() => {
    // Se não tem lurikProcess, não faz nada
    if (!lurikProcess || !lurikProcess.id || !lurikProcess.resourceId) {
      return;
    }

    let intervalId: NodeJS.Timeout | null = null;
    let isReportReady = false;

    const checkStatus = async () => {
      // Se já encontrou o relatório, para de verificar
      if (isReportReady) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }

      try {
        const statusReport = await checkReportStatus({
          idProcess: lurikProcess.id,
          resourceId: lurikProcess.resourceId,
          userId: user.id,
        });

        if (
          statusReport &&
          statusReport.reportCompanies &&
          statusReport.reportCompanies.length > 0
        ) {
          // Marca que encontrou o relatório
          isReportReady = true;
          setReportGenerated(true);

          // Para o intervalo imediatamente
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        }
      } catch (error) {
        // Em caso de erro, continua tentando
      }
    };

    intervalId = setInterval(checkStatus, 2000);
    checkStatus();

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [lurikProcess, user.id, checkReportStatus]);

  // Função para exportar XLSX
  const exportConsumptionReport = () => {
    if (!reportCompanies || !reportCompanies.length) return;
    const showSellerColumn = user?.userType !== "SELLER";
    const mappedResult = reportCompanies.map((item) => {
      return [
        item.cnpj || "",
        item.companyName || "",
        item.nomeFantasia || "",        
        item.includedAt
          ? new Date(item.includedAt).toLocaleDateString("pt-BR")
          : "",
        item.active ? "Ativa" : "Inativa",
        item.currentPlanVolume?.toLocaleString("pt-BR") || "0",
        ...(showSellerColumn ? [item.sellerName || ""] : []),
        item.initialPlan || "",
        item.currentPlan || "",
        item.defaultExtraction?.toLocaleString("pt-BR") || "0",
        item.googleAdsExtraction?.toLocaleString("pt-BR") || "0",
        item.metaAdsExtraction?.toLocaleString("pt-BR") || "0",
        item.enrichment?.toLocaleString("pt-BR") || "0",
        item.unitQuery?.toLocaleString("pt-BR") || "0",
        item.exceedingExtraction?.toLocaleString("pt-BR") || "0",
        item.totalExtraction?.toLocaleString("pt-BR") || "0",
        item.extractionQuantity?.toLocaleString("pt-BR") || "0",
        item.usersQuantity?.toLocaleString("pt-BR") || "0",
        item.currentPlanUpdatedAt
          ? new Date(item.currentPlanUpdatedAt).toLocaleDateString("pt-BR")
          : "",
      ];
    });

    const headers = [
      "CNPJ",
      "Razão Social",
      "Nome Fantasia",
      "Incluído Em",
      "Status",
      "Volume Plano Atual",
      ...(showSellerColumn ? ["Vendedor"] : []),
      "Plano Inicial",
      "Plano Atual",
      "Extração Padrão",
      "Extração Google Ads",
      "Extração Meta Ads",
      "Enriquecimento",
      "Consulta Unitária",
      "Excedentes",
      "Total Créditos",
      "n° de Extrações",
      "n° de Usuários",
      "Plano Atualizado em",
    ];

    let primaryColor = "723989";
    let contrastColor = "FFFFFFFF";
    let fgColor = "e8e4d6";

    if (resaleConfig) {
      if (resaleConfig.id !== "") {
        if (resaleConfig.theme.colors.primary.main !== "") {
          primaryColor = resaleConfig.theme.colors.primary.main;
        }
        if (resaleConfig.theme.colors.primary.contrastColor !== "") {
          contrastColor = resaleConfig.theme.colors.primary.contrastColor;
        }
      }
    }

    const fileName = generateGuid();
    
    xlsxGenerator(
      mappedResult,
      headers,
      fileName,
      primaryColor,
      contrastColor,
      fgColor
    );
  };

  // Colunas do DataGrid para o modal
  const reportCompaniesCols: GridColDef[] = useMemo(() => [
    {
      field: "cnpj",
      headerName: "CNPJ",
      headerAlign: "center",
      minWidth: 180,
      align: "center",
      renderCell(params) {
        return <div>{formatCnpj(String(params.value ?? ""))}</div>;
      },
    },
    {
      field: "companyName",
      headerName: "Razão Social",
      headerAlign: "center",
      minWidth: 150,
      align: "center",
      flex: 1,
    },
    {
      field: "fantasyName",
      headerName: "Nome Fantasia",
      headerAlign: "center",
      minWidth: 150,
      align: "center",
      flex: 1,
    },
    {
      field: "includedAt",
      headerName: "Incluído Em",
      headerAlign: "center",
      minWidth: 120,
      align: "center",
    },
    {
      field: "active",
      headerName: "Status",
      headerAlign: "center",
      minWidth: 80,
      align: "center",
      renderCell(params) {
        const isActive = params.value as boolean;
        return (
          <div
            style={{
              margin: "auto",
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              background: isActive
                ? theme.palette.success.main
                : theme.palette.error.main,
              boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.2)",
            }}
          />
        );
      },
    },
    {
      field: "currentPlanVolume",
      headerName: "Volume Plano Atual",
      headerAlign: "center",
      minWidth: 120,
      align: "center",
      renderCell(params) {
        const value = params.value;
        return <div>{typeof value === "number" ? value.toLocaleString("pt-BR") : "0"}</div>;
      },
    },
    ...(user.userType !== "SELLER"
      ? [
          {
            field: "sellerName",
            headerName: "Vendedor",
            headerAlign: "center" as const,
            minWidth: 120,
            align: "center" as const,
            flex: 0.8,
          },
        ]
      : []),
    {
      field: "initialPlan",
      headerName: "Plano Inicial",
      headerAlign: "center",
      minWidth: 120,
      align: "center",
    },
    {
      field: "currentPlan",
      headerName: "Plano Atual",
      headerAlign: "center",
      minWidth: 120,
      align: "center",
    },
    {
      field: "defaultExtraction",
      headerName: "Extração Padrão",
      headerAlign: "center",
      minWidth: 120,
      align: "center",
      renderCell(params) {
        const value = params.value;
        return <div>{typeof value === "number" ? value.toLocaleString("pt-BR") : "0"}</div>;
      },
    },
    {
      field: "googleAdsExtraction",
      headerName: "Extração Google Ads",
      headerAlign: "center",
      minWidth: 120,
      align: "center",
      renderCell(params) {
        const value = params.value;
        return <div>{typeof value === "number" ? value.toLocaleString("pt-BR") : "0"}</div>;
      },
    },
    {
      field: "metaAdsExtraction",
      headerName: "Extração Meta Ads",
      headerAlign: "center",
      minWidth: 120,
      align: "center",
      renderCell(params) {
        const value = params.value;
        return <div>{typeof value === "number" ? value.toLocaleString("pt-BR") : "0"}</div>;
      },
    },
    {
      field: "enrichment",
      headerName: "Enriquecimento",
      headerAlign: "center",
      minWidth: 120,
      align: "center",
      renderCell(params) {
        const value = params.value;
        return <div>{typeof value === "number" ? value.toLocaleString("pt-BR") : "0"}</div>;
      },
    },
    {
      field: "unitQuery",
      headerName: "Consulta Unitária",
      headerAlign: "center",
      minWidth: 120,
      align: "center",
      renderCell(params) {
        const value = params.value;
        return <div>{typeof value === "number" ? value.toLocaleString("pt-BR") : "0"}</div>;
      },
    },
    {
      field: "exceedingExtraction",
      headerName: "Excedentes",
      headerAlign: "center",
      minWidth: 120,
      align: "center",
      renderCell(params) {
        const value = params.value;
        return <div>{typeof value === "number" ? value.toLocaleString("pt-BR") : "0"}</div>;
      },
    },
    {
      field: "totalExtraction",
      headerName: "Total Créditos",
      headerAlign: "center",
      minWidth: 120,
      align: "center",
      renderCell(params) {
        const value = params.value;
        return <div>{typeof value === "number" ? value.toLocaleString("pt-BR") : "0"}</div>;
      },
    },
    {
      field: "extractionQuantity",
      headerName: "n° de Extrações",
      headerAlign: "center",
      minWidth: 120,
      align: "center",
      renderCell(params) {
        const value = params.value;
        return <div>{typeof value === "number" ? value.toLocaleString("pt-BR") : "0"}</div>;
      },
    },
    {
      field: "usersQuantity",
      headerName: "n° de Usuários",
      headerAlign: "center",
      minWidth: 120,
      align: "center",
      renderCell(params) {
        const value = params.value;
        return <div>{typeof value === "number" ? value.toLocaleString("pt-BR") : "0"}</div>;
      },
    },
    {
      field: "currentPlanUpdatedAt",
      headerName: "Plano Atualizado em",
      headerAlign: "center",
      minWidth: 120,
      align: "center",
    },
  ], [theme, user.userType]);

  // Dados da tabela para o DataGrid
  const tableData = useMemo(() => {
    if (!reportCompanies) return [];

    return reportCompanies.map(
      (reportCompany: IReportCompanies, index: number) => ({
        id: reportCompany.companyId || `company-${index}`,
        cnpj: reportCompany.cnpj || "",
        companyName: reportCompany.companyName || "",
        fantasyName: reportCompany.nomeFantasia || "-",
        active: reportCompany.active,
        includedAt: reportCompany.includedAt
          ? new Date(reportCompany.includedAt).toLocaleDateString("pt-BR")
          : "",
        currentPlanVolume: reportCompany.currentPlanVolume || 0,
        sellerName: reportCompany.sellerName || "",
        initialPlan: reportCompany.initialPlan || "",
        currentPlan: reportCompany.currentPlan || "",
        defaultExtraction: reportCompany.defaultExtraction || 0,
        googleAdsExtraction: reportCompany.googleAdsExtraction || 0,
        metaAdsExtraction: reportCompany.metaAdsExtraction || 0,
        enrichment: reportCompany.enrichment || 0,
        unitQuery: reportCompany.unitQuery || 0,
        exceedingExtraction: reportCompany.exceedingExtraction || 0,
        totalExtraction: reportCompany.totalExtraction || 0,
        extractionQuantity: reportCompany.extractionQuantity || 0,
        usersQuantity: reportCompany.usersQuantity || 0,
        currentPlanUpdatedAt: reportCompany.currentPlanUpdatedAt
          ? new Date(reportCompany.currentPlanUpdatedAt).toLocaleDateString(
              "pt-BR"
            )
          : "",
      })
    );
  }, [reportCompanies]);

  return (
    <Box>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Relatório Analítico de consumo
          </Typography>
          <Button
            sx={{ padding: "10px" }}
            color="inherit"
            startIcon={
              <FilterList sx={{ color: "rgba(255, 255, 255, 0.6)" }} />
            }
            onClick={clearAllFilters}
          >
            Limpar filtros
          </Button>
        </Toolbar>
      </AppBar>
      <Box p={3}>
        <Grid container justifyContent={"center"} spacing={2}>
          <Grid item xs={12} md={6} lg={4}>
            <Autocomplete
              options={companyList}
              getOptionLabel={(option) => {
                if (!option || !option.company) return "";
                return `${nameAbbreviation(option.company.razaoSocial)}${
                  option.company.nomeFantasia
                    ? ` / ${nameAbbreviation(option.company.nomeFantasia)}`
                    : ""
                } - ${formatCnpj(option.company.cnpj)}`;
              }}
              value={selectedCompanyOption}
              onChange={(_, newValue) => {
                setSelectedCompanyOption(newValue);
                if (newValue) {
                  selectCompany({
                    ...newValue,
                    resaleConfig: Array.isArray(newValue.resaleConfig)
                      ? newValue.resaleConfig[0]
                      : newValue.resaleConfig,
                  });
                } else {
                  selectCompany(undefined);
                }
                resetConsumptionReport();
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Selecione a empresa"
                  variant="outlined"
                  fullWidth
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option?.company?.id === value?.company?.id
              }
            />
          </Grid>
          <Grid item>
            <LocalizationProvider
              dateFormats={{
                fullDate: "dd/MM/yyyy",
              }}
              dateAdapter={AdapterDateFns}
              adapterLocale={ptBR}
            >
              <DatePicker
                value={consumptionInfoStartDate}
                onChange={(newValue) => {
                  if (newValue) {
                    setConsumptionInfoStartDate(newValue);
                  }
                }}
                disableFuture
                views={["year", "month"]}
                label="Selecione o período inicial"
                slotProps={{
                  day: {
                    sx: {
                      "&.MuiPickersDay-root.Mui-selected": {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        "&:hover": {
                          backgroundColor: darkenColor(
                            theme.palette.primary.main,
                            10
                          ),
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item>
            <LocalizationProvider
              dateFormats={{
                fullDate: "dd/MM/yyyy",
              }}
              dateAdapter={AdapterDateFns}
              adapterLocale={ptBR}
            >
              <DatePicker
                value={consumptionInfoEndDate}
                onChange={(newValue) => {
                  if (newValue) {
                    setConsumptionInfoEndDate(newValue);
                  }
                }}
                disableFuture
                views={["year", "month"]}
                label="Selecione o período final"
                slotProps={{
                  day: {
                    sx: {
                      "&.MuiPickersDay-root.Mui-selected": {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        "&:hover": {
                          backgroundColor: darkenColor(
                            theme.palette.primary.main,
                            10
                          ),
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              startIcon={
                <AssessmentIcon sx={{ color: "rgba(255, 255, 255, 0.6)" }} />
              }
              disabled={!consumptionInfoStartDate || !consumptionInfoEndDate}
              variant="contained"
              sx={{
                "&:hover": {
                  backgroundColor: darkenColor(theme.palette.primary.main, 10),
                },
                color: theme.palette.primary.contrastText,
              }}
              onClick={async () => {
                setReportGenerated(false);
                await getPeriodReportCompanies({
                  userId: user.id,
                  limit: pagination.pageSize,
                  page: pagination.pageIndex,
                  filters: {
                    companyId: selectedCompany?.company.id ?? "",
                    startDate: consumptionInfoStartDate!,
                    endDate: consumptionInfoEndDate!,
                  },
                });
              }}
            >
              Gerar relatório
            </Button>
          </Grid>

          {/* Botões após gerar relatório */}
          {reportGenerated &&
            reportCompanies &&
            reportCompanies.length > 0 && (
              <Grid item xs={12} container spacing={2} justifyContent="center">
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenReportDialog(true)}
                  >
                    Abrir relatório analítico
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={exportConsumptionReport}
                  >
                    Exportar XLSX
                  </Button>
                </Grid>
              </Grid>
            )}
        </Grid>
      </Box>
      {/* MODAL DE RELATÓRIO ANALÍTICO */}
      <Dialog
        fullScreen
        open={openReportDialog}
        onClose={() => setOpenReportDialog(false)}
      >
        <AppBar sx={{ position: "relative" , boxShadow: "none"}}>
          <Toolbar>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Relatório Analítico de Consumo
            </Typography>
            <Button
              color="inherit"
              onClick={() => setOpenReportDialog(false)}
            >
              FECHAR
            </Button>
          </Toolbar>
        </AppBar>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 64px)',
            overflow: 'hidden'
          }}
        >
                 
          
          {/* Container da tabela */}
          <Box
            ref={tableContainerRef}
            sx={{
              flex: 1,
              overflow: 'auto',
              position: 'relative',
            }}
          >
            {/* TABELA */}
            {reportCompanies && reportCompanies.length > 0 && (
              <DataGrid
                showCellVerticalBorder
                disableColumnFilter
                disableColumnMenu
                rowSelection={false}
                localeText={dataGridTexts}
                pagination={undefined}
                rows={tableData}
                columns={reportCompaniesCols}
                sx={{
                  "& .MuiDataGrid-columnHeader": {
                    backgroundColor: theme.palette.primary.main,
                    color:
                      company?.resale === true || company?.resaleId
                        ? theme.palette.primary.contrastText
                        : "#FFF",
                  },
                  "& .MuiDataGrid-cell": {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                  "& .MuiDataGrid-cell:focus": {
                    outline: 0,
                  },
                  "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                    outline: "none !important",
                  },
                  minWidth: "100%",
                  border: 'none',
                }}
              />
            )}
          </Box>

          {/* Barra de rolagem inferior */}
          <Box
            ref={bottomScrollRef}
            sx={{
              overflowX: 'auto',
              overflowY: 'hidden',
              backgroundColor: theme.palette.grey[100],
              borderTop: `1px solid ${theme.palette.divider}`,
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.grey[400],
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.grey[200],
              },
            }}
          >
            <Box sx={{ minWidth: '100%', height: '1px' }} /> {/* Espaço para a barra de rolagem */}
          </Box>
        </Box>


      </Dialog>
    </Box>
  );
};